import { SkillData, SkillType, PokemonType } from "./battle";

/**
 * Event types that can be emitted by Pokemon during battle
 */
export enum PokemonEventType {
  USE_SKILL = "USE_SKILL",
  FAINT = "FAINT",
  ATTACK = "ATTACK",
  DEAL_DAMAGE = "DEAL_DAMAGE",
  TAKE_DAMAGE = "TAKE_DAMAGE",
  MOVE = "MOVE",
  HEAL = "HEAL",
  CHARGE = "CHARGE",
  KILL = "KILL",
  ALLY_DEATH = "ALLY_DEATH",
}

/**
 * Base event payload
 */
export interface PokemonEventPayload {
  tick: number;
  sourceId: number;
}

/**
 * Payload for USE_SKILL event
 */
export interface UseSkillEventPayload extends PokemonEventPayload {
  skillId: string;
  skillType: SkillType[];
  skillData: SkillData;
  targetId?: number;
}

/**
 * Payload for DEAL_DAMAGE and TAKE_DAMAGE events
 */
export interface DamageEventPayload extends PokemonEventPayload {
  amount: number;
  targetId: number;
  skillId?: string;
  isFatal: boolean;
}

/**
 * Payload for HEAL event
 */
export interface HealEventPayload extends PokemonEventPayload {
  amount: number;
  targetId: number;
}

/**
 * Payload for MOVE event
 */
export interface MoveEventPayload extends PokemonEventPayload {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

/**
 * Payload for FAINT event
 */
export interface FaintEventPayload extends PokemonEventPayload {
  killerId?: number;
}

/**
 * Payload for KILL event
 */
export interface KillEventPayload extends PokemonEventPayload {
  victimId: number;
}

/**
 * Payload for ALLY_DEATH event
 */
export interface AllyDeathEventPayload extends PokemonEventPayload {
  allyId: number;
  killerId?: number;
}

/**
 * Payload for CHARGE event
 */
export interface ChargeEventPayload extends PokemonEventPayload {
  skillId: string;
  durationTicks: number;
}

/**
 * Union type for all event payloads
 */
export type PokemonEventPayloadMap = {
  [PokemonEventType.USE_SKILL]: UseSkillEventPayload;
  [PokemonEventType.FAINT]: FaintEventPayload;
  [PokemonEventType.ATTACK]: PokemonEventPayload;
  [PokemonEventType.DEAL_DAMAGE]: DamageEventPayload;
  [PokemonEventType.TAKE_DAMAGE]: DamageEventPayload;
  [PokemonEventType.MOVE]: MoveEventPayload;
  [PokemonEventType.HEAL]: HealEventPayload;
  [PokemonEventType.CHARGE]: ChargeEventPayload;
  [PokemonEventType.KILL]: KillEventPayload;
  [PokemonEventType.ALLY_DEATH]: AllyDeathEventPayload;
};

/**
 * Event listener callback type
 */
export type PokemonEventListener<T extends PokemonEventType> = (
  payload: PokemonEventPayloadMap[T],
) => void;

/**
 * Filters for reactive triggers
 */
export interface ReactiveTriggerFilters {
  skill_type?: SkillType;
  skill_id?: string;
  min_damage?: number;
  source_is_self?: boolean;
  target_is_self?: boolean;
  pokemon_type?: PokemonType;
}

/**
 * Reactive trigger action types
 */
export type ReactiveTriggerAction = "cooldown_reset";

/**
 * Reactive trigger configuration for skills
 */
export interface ReactiveTriggerConfig {
  event: PokemonEventType;
  chance: number; // 0-1
  action: ReactiveTriggerAction;
  filters?: ReactiveTriggerFilters;
}

